import { useCallback, useEffect, useMemo, useState } from "react";

import { getIsAgeVerificationNotExpired } from "@jti/fe-common";

import { AGE_VERIFICATION_METHOD } from "../../components/HardAgeVerificationPopup/HardAgeVerificationPopup.types";
import { useUser } from "../useUser";

type UseHavOverlayConditions = ({
  ignoreUrls,
  ageVerificationMethod,
}: {
  ignoreUrls: string[];
  ageVerificationMethod: AGE_VERIFICATION_METHOD;
}) => {
  shouldRenderHAV: boolean;
  shouldTriggerAv: boolean;
  isRegisterPage: boolean;
};

const getIsPageContainRoute = (route: string) => window.location.href.includes(route);

export const validateAgeVerificationExpirationDate = (timestamp: string | undefined): boolean => {
  if (!timestamp) {
    return false;
  }

  const ageVerificationDate = new Date(timestamp);
  const currentDate = new Date();

  return ageVerificationDate > currentDate;
};

export const useHavOverlayConditions: UseHavOverlayConditions = ({ ignoreUrls, ageVerificationMethod }) => {
  const isRegisterPage = getIsPageContainRoute("/register");

  const isAgeVerificationNotExpired = getIsAgeVerificationNotExpired();
  const { isUserLoggedIn, user } = useUser();

  const getIsRouteInIgnoreList = useCallback(
    () => ignoreUrls.some((skipHavUrl) => window.location.href.includes(skipHavUrl)),
    [ignoreUrls]
  );

  const [isRouteInIgnoreList, setIsRouteInIgnoreList] = useState(getIsRouteInIgnoreList());

  const isPassAvMethod = ageVerificationMethod === AGE_VERIFICATION_METHOD.PASS;
  const isDiiaAvMethod = ageVerificationMethod === AGE_VERIFICATION_METHOD.DIIA_ASYNC;

  const isUserAgeVerified = isUserLoggedIn && user?.is_age_verified;
  const isUserNotSet = (isUserLoggedIn && user?.email === "") || user === null;

  const shouldHidePass = (isPassAvMethod && isUserAgeVerified) || isUserNotSet || isRouteInIgnoreList;

  const isDiiaVerified =
    isDiiaAvMethod &&
    ((isUserLoggedIn && validateAgeVerificationExpirationDate(user?.ageVerificationExpirationDate ?? undefined)) ||
      (!isUserLoggedIn && isAgeVerificationNotExpired));
  const shouldHideDiia = isDiiaVerified || isRouteInIgnoreList;

  const shouldRenderHAV = isPassAvMethod ? !isAgeVerificationNotExpired && !shouldHidePass : !shouldHideDiia;

  useEffect(() => {
    const handleHashChange = () => {
      setIsRouteInIgnoreList(getIsRouteInIgnoreList());
    };

    // NOTE: This is needed for cases when user is redirected in one fragment scope
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const shouldTriggerAv = useMemo(() => isRegisterPage && isPassAvMethod, [isPassAvMethod, isRegisterPage]);

  return { shouldRenderHAV, shouldTriggerAv, isRegisterPage };
};
